import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

type CanvasType = 'main' | 'mask'

type Store = {
  canvases: Record<CanvasType, HTMLCanvasElement | null>
  captureFunctions: Record<CanvasType, (() => Promise<File>) | null>
  setCanvas: (id: CanvasType, canvas: HTMLCanvasElement | null) => void
  setCaptureFunction: (id: CanvasType, fn: (() => Promise<File>) | null) => void
  capture: (id: CanvasType) => Promise<File>
}

const canvasStore = create<Store>((set, get) => ({
  canvases: {
    main: null,
    mask: null,
  },
  captureFunctions: {
    main: null,
    mask: null,
  },
  setCanvas: (id, canvas) => set({ canvases: { ...get().canvases, [id]: canvas } }),
  setCaptureFunction: (id, fn) =>
    set({ captureFunctions: { ...get().captureFunctions, [id]: fn } }),
  capture: async (id) => {
    const captureFn = get().captureFunctions[id]
    if (captureFn) return captureFn()

    const current = get().canvases[id]
    if (!current) throw new Error('Canvas not found')

    switch (id) {
      case 'main':
        return captureMain(current)
      case 'mask':
        return captureMask(current)
    }
  },
}))

export const useCanvasStore = () => {
  return canvasStore(useShallow((s) => s))
}

const captureMain = (canvas: HTMLCanvasElement) => {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to create blob'))),
      'image/png',
    )
  }).then((blob) => new File([blob], 'canvas.png', { type: 'image/png' }))
}

const captureMask = (canvas: HTMLCanvasElement) => {
  return new Promise<Blob>((resolve, reject) => {
    const ctx = canvas.getContext('2d')!
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    for (let i = 0; i < imageData.data.length; i += 4) {
      const alpha = imageData.data[i + 3]!
      imageData.data[i] = alpha
      imageData.data[i + 1] = alpha
      imageData.data[i + 2] = alpha
      imageData.data[i + 3] = 255
    }

    const offscreen = document.createElement('canvas')
    offscreen.width = canvas.width
    offscreen.height = canvas.height
    offscreen.getContext('2d')!.putImageData(imageData, 0, 0)

    offscreen.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to create blob'))),
      'image/png',
    )
  }).then((blob) => new File([blob], 'mask.png', { type: 'image/png' }))
}
